import { useTranslation } from "react-i18next";
import { EDITABLE_ZONE_KINDS, type EditableZoneKind } from "../../../../api/rest";
import type { EditableZone } from "./zoneGeometry";
import css from "../ZonesSection.module.css";

interface Props {
  zone: EditableZone | null;
  disabled: boolean;
  onKind: (k: EditableZoneKind) => void;
  onMaxSpeed: (v: number | null) => void;
  onDirection: (rad: number | null) => void;
  onDelete: () => void;
}

/** Decimal comma or point. `undefined` = not a number: leave the value as it was. */
const parseNum = (s: string): number | null | undefined => {
  if (s.trim() === "") return null;
  const v = Number(s.trim().replace(",", "."));
  return Number.isFinite(v) ? v : undefined;
};

/** Speed limit: empty or 0 means no limit (null); a negative or non-number is refused
 *  (the server answers 400 for anything <= 0), so the field keeps its previous value. */
export const parseMaxSpeed = (s: string): number | null | undefined => {
  const v = parseNum(s);
  if (v == null) return v;
  if (v === 0) return null;
  return v > 0 ? v : undefined;
};

export function ZonePropertiesPanel({ zone, disabled, onKind, onMaxSpeed, onDirection, onDelete }: Props) {
  const { t, i18n } = useTranslation();
  if (!zone) return <p className={css.note}>{t("structure.zones.props.none")}</p>;
  const fmt = (v: number | null) => (v == null ? "" : v.toLocaleString(i18n.language, { maximumFractionDigits: 2 }));
  return (
    <div className={css.props} data-testid="zone-props">
      <code className={css.propsId}>{zone.id}</code>
      <label className={css.field}>
        <span>{t("structure.zones.props.kind")}</span>
        <select value={zone.kind} disabled={disabled} onChange={(e) => onKind(e.target.value as EditableZoneKind)}>
          {EDITABLE_ZONE_KINDS.map((k) => <option key={k} value={k}>{t(`zone.kind.${k}`)}</option>)}
        </select>
      </label>
      <label className={css.field}>
        <span>{t("structure.zones.props.maxSpeed")}</span>
        <input inputMode="decimal" disabled={disabled} defaultValue={fmt(zone.maxSpeedMps)} key={`${zone.id}-speed-${zone.maxSpeedMps}`}
               onBlur={(e) => {
                 const v = parseMaxSpeed(e.target.value);
                 if (v === undefined) e.target.value = fmt(zone.maxSpeedMps);
                 else onMaxSpeed(v);
               }} />
      </label>
      {zone.kind === "one_way" && (
        <label className={css.field}>
          <span>{t("structure.zones.props.direction")}</span>
          <input inputMode="decimal" disabled={disabled} key={`${zone.id}-dir-${zone.directionRad}`}
                 defaultValue={zone.directionRad == null ? "" : fmt((zone.directionRad * 180) / Math.PI)}
                 onBlur={(e) => {
                   const d = parseNum(e.target.value);
                   if (d === undefined) e.target.value = zone.directionRad == null ? "" : fmt((zone.directionRad * 180) / Math.PI);
                   else onDirection(d == null ? null : (d * Math.PI) / 180);
                 }} />
        </label>
      )}
      {!disabled && (
        <button type="button" className={css.danger} onClick={onDelete}>{t("structure.zones.props.delete")}</button>
      )}
    </div>
  );
}
